//@ts-check
import 'bootstrap/js/dist/tab'; // ! [REVISIT] needed?
// ? [REVISIT] what the reason for importing stylesheet like this; isn't the reason to follow Slate's file structure, so it does the import for us?
import '../../../styles/templates/product.scss';

import React, { StrictMode, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { ApolloProvider } from '@apollo/client';

import { isLocal, isProduction } from '../../utils/environment';
import App from './components/PDPContainer';
import ErrorBoundary, { ExplicitDeadEnd } from '../../utils/react/ErrorBoundary';
import defaultClient from '../_shared/apolloClient';

import { heapTrack, retryPromise } from '../../utils/helpers';
import { Routes, Route, BrowserRouter as Router, BrowserRouter } from 'react-router-dom';
import { first } from 'lodash-es';
import ProductReviews from './components/ProductReviews';
import ProductReviewProvider from './components/ProductReviews/ProductReviewContext';

const loadFallbackPDP = async () => {
  try {
    const url = `//${window.location.host}${window.location.pathname}?view=error-fallback`;
    const getContent = () => fetch(url).then((response) => response.text());
    const fallbackContent = await retryPromise(getContent, 3); //, { delay: 1000 }); // should we bother delaying?
    const main = document.getElementById('MainContent') || document.getElementsByTagName('main')[0] || document.body;
    main.innerHTML = fallbackContent;
  } catch (err) {
    heapTrack('pdp failed to load fallback content', {
      err: (err as Error)?.message,
      pathname: window.location.pathname,
    });
  }
};

const onError = (err: Error) => {
  heapTrack('pdp crash', { url: window.location.pathname, err: (err && err.message) || undefined });
  if (isProduction) loadFallbackPDP();
};

const targetElement = document.getElementById('REACT-PDP');
const render = (Component: () => JSX.Element | null) => {
  return ReactDOM.render(
    <StrictMode>
      <ErrorBoundary className="centered-message" key={(!isLocal || new Date()).toString()} onError={onError}>
        <ApolloProvider client={defaultClient}>
          <Router>
            <Routes>
              <Route path="/:shopifyRoot/products/:handle/*" element={<Component />} />
              <Route path="/:shopifyRoot/products/:handle" element={<Component />} />
              <Route path="/products/:handle/*" element={<Component />} />
              <Route path="/products/:handle" element={<Component />} />
              <Route path="/products_preview" element={<Component />} />
              <Route path="*" element={<ExplicitDeadEnd />} />
            </Routes>
          </Router>
        </ApolloProvider>
      </ErrorBoundary>
    </StrictMode>,
    targetElement
  );
};

// ! [REVISIT] Extracting App Pixlee & other PDP sections to their own files
/*
When you dynamically import a module, it automatically makes that module ineligible for tree shaking. The result of the dynamic import is an object with all the exports of the module. Due to the dynamic nature of JavaScript, webpack can’t easily determine which exports will be used, so webpack can’t do any tree shaking. For more details on tree shaking behavior for dynamic imports, checkout this great description from the webpack team.
https://medium.com/@christiango/the-unexpected-impact-of-dynamic-imports-on-tree-shaking-ddadeb135dd7
https://github.com/webpack/webpack.js.org/issues/2684
*/
// ! [REVISIT] `export Builder from './BuilderLazy';`
const renderPixleeFeedPDP = () => {
  const targetElementPixlee = document.getElementById('PixleeFeedPDP');
  const Component = lazy(
    () =>
      import(
        /* webpackChunkName: "PixleeFeed" */
        '../_shared/pixlee/PixleeFeed'
      )
  );
  if (targetElementPixlee) {
    const DefaultPostsPerRequest = '12';

    const productTypeArray = window.server_pdp_data?.product.type.split(':').map((part) => part.toLowerCase()) || [];
    const pdpGender = first(productTypeArray);

    const pixleeFilters =
      pdpGender === 'men'
        ? '{"in_categories":[69904235],"not_in_categories":[70025262]}'
        : pdpGender === 'women'
        ? '{"not_in_categories":[69904235, 70025262]}'
        : '{"not_in_categories":[70025262]}';

    const styleId = window.server_pdp_data?.product.handle.split('-')[0];
    const requestURL = `https://distillery.pixlee.com/api/v2/albums/from_title?filters=${pixleeFilters}&title=${styleId}&album_type=category&page=1&per_page=${DefaultPostsPerRequest}&api_key=TB6jIdhJVSuV0LWK7elH`;

    ReactDOM.render(
      <ErrorBoundary className="centered-message" key={(!isLocal || new Date()).toString()}>
        <Suspense fallback={null}>
          <ApolloProvider client={defaultClient}>
            <Component isPDP requestURL={requestURL} />
          </ApolloProvider>
        </Suspense>
      </ErrorBoundary>,
      targetElementPixlee
    );
  }
};

const renderProductReviews = () => {
  const mainMountNode = document.querySelector('[react-render-target="product-reviews"]');
  if (mainMountNode)
    ReactDOM.render(
      // BrowserRouter: This is necessary for the sorting dropdown to function properly with React Router
      <BrowserRouter>
        <ProductReviewProvider>
          <ProductReviews />
        </ProductReviewProvider>
      </BrowserRouter>,
      mainMountNode
    );
  else console.warn('missing render target: product-reviews');
};

const renderPdpPage = () => {
  render(App);
  renderProductReviews();
  renderPixleeFeedPDP();
  if (!isProduction && new URLSearchParams(window.location.search).get('forceFallback') !== null) loadFallbackPDP();
};

export { renderPdpPage };

// if (process.env.NODE_ENV === 'development' && module?.hot) {
//   module.hot.accept();
// }
